import { badKeywords } from './bad-keywords';
import { getContextCategory, getContextKeywords } from './context';
import {
  isInGoogleAdsIframe,
  resizeOwnIframe,
  thisIframeInParent,
} from './iframeHelpers';
import { checkPageForKeywords } from './KeywordsManager';
import { fireOnFirstInView } from './viewportUtils';

import { CampaignResponseBody } from '@flipp/shopper-ad-web-schemas';
import {
  AnalyticsQueue,
  asNumber,
  createDecisionRequestContextBeacon,
  createDecisionRequestMadeBeacon,
  createDecisionResponseFailureBeacon,
  createDecisionResponseSuccessBeacon,
  createExperienceMeasuredTestBeacon,
  createSlotImpressionExperienceBeacon,
  createContainerViewableImpressionBeacon,
  createV2BeaconGenerator,
  environment,
  executeBeaconCallback,
  fireClickTracker,
  getCountry,
  getPostalCode,
  getIntegrationType,
  getSessionId,
  getSiteDomain,
  getUserKey,
  isIntersectionObserverSupported,
  KevelCustomData,
  KevelPlacement,
  KevelRequest,
  LoaderOptions,
  logError,
  Logger,
  logUnknownError,
  PostMessageEvent,
  Tags,
  isInIframe,
} from '@flipp/shopper-ad-web-shared';
import Experience from './Experience';
import {
  setStyleForAdContainers,
  setStyleForIframeContainers,
  setStyleForRootElements,
} from './GAMHelpers';
import {
  MOBILE_SWIPE_AUTO_ENGAGE_DEFAULT_INTERVAL_MS,
  MobileSwipeExperience,
  resolveMobileSwipeCtaUrl,
  resolveMobileSwipeEvTimerSeconds,
} from './MobileSwipeExperience';
import { resolveMobileSwipeDynamicModule } from './MobileSwipeDynamicModule';

export const NETWORK_ID = __kevelNetwork;
export const AD_TYPES = [4309, 641];
export const TARGET_NAME = 'inline';
const DEFAULT_EXPERIENCE_COUNT = 1;
const DEFAULT_DWELL_TIMER_MS = 3000;
const COUNTDOWN_STEPS = 3;
const AUTO_EXPAND_LIMIT = 1;
const FIXED_HEIGHT_UNIT_FALLBACK_CONTENT_CODE = 'familydollarFallback';

export class Slot {
  private decisionPayload?: CampaignResponseBody;
  private readonly experiences: Experience[];
  private clickTrackerSent = false;
  private readonly markers: {
    el: HTMLDivElement;
    active: boolean;
    index: number;
  }[];
  private requested: boolean;
  private fallbackRequested: boolean = false;

  private experienceLimit: number;

  readonly targetSelector: string;
  private targetEl?: HTMLElement | null;
  private readonly publisherNameIdentifier: string;
  private readonly siteId: number;
  private readonly zoneIds: number[];
  private readonly options: LoaderOptions;
  private dwellObserver: IntersectionObserver | null = null;
  private scrollBeyondObserver: IntersectionObserver | null = null;
  private hoverObserver: IntersectionObserver | null = null;
  private fullyVisibleExperiences: number[];

  private countdownExperience?: number;
  private countdownStart?: number;
  private countdownInterval?: number;

  private readonly startCountdownFunctions = new Map<number, () => null>();

  private readonly slotFillResultCallback?: (filled: boolean) => void;

  private containerViewableObserver?: IntersectionObserver;
  private hasFiredContainerBeacon = false;
  private containerViewableTimer?: number;

  private latestDecisionData?: CampaignResponseBody;
  private readonly v2BeaconGenerator = createV2BeaconGenerator(() => {
    const decision = this.latestDecisionData?.decisions?.inline?.[0];
    const loc = this.latestDecisionData?.location;
    const resolvedSigningKey =
      ('__v2SigningKey' as string) === 'undefined'
        ? undefined
        : '__v2SigningKey';
    return {
      v2SchemaEndpoint: '__v2SchemaEndpoint',
      signingKey: resolvedSigningKey,
      decision: decision
        ? {
            campaignId: decision.campaignId,
            advertiserId: decision.advertiserId,
            creativeId: decision.creativeId,
            flightId: decision.flightId,
            adId: decision.adId,
            siteId: asNumber(this.siteId),
            zoneId: asNumber(this.zoneIds[0]),
          }
        : undefined,
      site: getSiteDomain(this.options.overrideHostname),
      country: loc?.country,
      userPostalCode: loc?.postal_code,
      uid: getUserKey(this.options) ?? undefined,
      storefrontConfig: decision?.storefront
        ? {
            ...decision.storefront,
            is_fallback: decision.storefront.is_fallback ?? undefined,
          }
        : undefined,
      // Mobile Swipe's publication is separate on V1 decisions
      publicationGlobalId: this.options.mobileSwipe
        ? decision?.mobileSwipePublication?.global_id ??
          decision?.publication?.global_id
        : decision?.publication?.global_id,
      integrationType: getIntegrationType(this.options),
      startCompact: !!this.options.startCompact,
      fixedHeightUnit: !!this.options.fixedHeightUnit,
      mobileSwipe: !!this.options.mobileSwipe,
      appVersion: '__appVersion',
    };
  });

  constructor(
    targetSelector: string,
    publisherNameIdentifier: string,
    siteId: number,
    zoneIds: readonly number[],
    options: Readonly<LoaderOptions>,
  ) {
    this.targetSelector = targetSelector;
    this.publisherNameIdentifier = publisherNameIdentifier
      ? publisherNameIdentifier.toString()
      : 'undefined';
    this.siteId = asNumber(siteId);
    this.zoneIds = zoneIds.map(id => asNumber(id));
    this.options = options;
    this.requested = false;
    this.experiences = [];
    this.markers = [];
    const url = new URL(window.location.toString());
    this.experienceLimit =
      Number(url.searchParams.get('flipp-experience-limit')) ||
      options.experienceLimit ||
      DEFAULT_EXPERIENCE_COUNT;
    this.fullyVisibleExperiences = [];

    if (this.options.fixedHeightUnit) {
      window.addEventListener('message', this.handleMessage);
    }

    this.syncDwellObservers();
    this.slotFillResultCallback =
      typeof options.slotFillResultCallback === 'function'
        ? options.slotFillResultCallback
        : undefined;
  }

  /**
   * (Re)builds the dwell/scroll/hover observers to match the current value of
   * `options.fixedHeightUnit`. Fixed height units drive expansion from inside
   * the iframe, so they run without any of these observers.
   */
  private syncDwellObservers(): void {
    if (this.options.fixedHeightUnit || !isIntersectionObserverSupported()) {
      this.dwellObserver?.disconnect();
      this.scrollBeyondObserver?.disconnect();
      this.hoverObserver?.disconnect();
      this.dwellObserver = null;
      this.scrollBeyondObserver = null;
      this.hoverObserver = null;
      return;
    }

    this.dwellObserver = new IntersectionObserver(this.observerCallback, {
      rootMargin: '-70% 0px -5% 0px',
    });

    this.scrollBeyondObserver = new IntersectionObserver(
      this.scrollBeyondCallback,
      {
        rootMargin: '-95% 0px 0px 0px',
      },
    );
    this.hoverObserver = new IntersectionObserver(this.hoverCallback, {
      threshold: 1.0,
    });
  }

  private shouldSendV2Beacons(customData?: Readonly<KevelCustomData>): boolean {
    return !!(
      customData?.enableV2Schema ||
      this.options.enableV2Schema ||
      this.options.mobileSwipe
    );
  }

  private shouldSkipV1Beacon(isSendingV2: boolean): boolean {
    return isSendingV2 && !!this.options.mobileSwipe;
  }

  private applyKevelFeatureFlags(data: Readonly<CampaignResponseBody>): void {
    const customData = this.getKevelCustomData(data);
    if (!customData) {
      return;
    }
    // Only true is a force; false is silence
    if (customData.mobileSwipe === true) {
      this.options.mobileSwipe = true;
    }
    if (
      this.options.fixedHeightUnit === undefined &&
      typeof customData.fixedHeightUnit === 'boolean'
    ) {
      this.setFixedHeightUnit(customData.fixedHeightUnit);
    }
  }

  private getKevelCustomData(
    data: Readonly<CampaignResponseBody>,
  ): KevelCustomData | undefined {
    return data.decisions?.inline?.[0]?.contents?.[0]?.data?.customData as
      | KevelCustomData
      | undefined;
  }

  private setFixedHeightUnit(enabled: boolean): void {
    const isEnabled = !!this.options.fixedHeightUnit;
    if (isEnabled === enabled) {
      return;
    }
    this.options.fixedHeightUnit = enabled;
    if (enabled) {
      window.addEventListener('message', this.handleMessage);
    } else {
      window.removeEventListener('message', this.handleMessage);
    }

    this.syncDwellObservers();
  }

  private readonly handleMessage = (event: MessageEvent) => {
    const message = event.data as { type: PostMessageEvent };
    const messageType = message.type;
    if (messageType !== PostMessageEvent.REQUEST_NEW_EXPERIENCE) {
      return;
    }
    if (this.fallbackRequested) {
      return;
    }
    this.fallbackRequested = true;
    const iframe = this.targetEl?.querySelector('iframe');
    if (iframe) {
      iframe.remove();
    }
    const taboolaFooter = document.querySelector('.taboola-footer');
    if (taboolaFooter) {
      taboolaFooter.remove();
    }
    this.requested = false;
    this.options.fixedHeightUnit = true;
    this.options.campaigns = undefined;
    this.request();
  };

  readonly startCountdownWithMap = (i: number): (() => null) => {
    if (!this.startCountdownFunctions.has(i)) {
      this.startCountdownFunctions.set(i, () => this.startCountdown(i));
    }
    return this.startCountdownFunctions.get(i) ?? (() => null);
  };

  private callSlotFillResultCallback(filled: boolean): void {
    if (this.options.slotFillResultCallback) {
      this.options.slotFillResultCallback(filled);
    }
  }

  tickCountdown = () => {
    if (
      this.countdownStart === undefined ||
      this.countdownExperience === undefined
    ) {
      return null;
    }

    const elapsed = Date.now() - this.countdownStart;
    const totalDuration = this.getDwellDuration(this.countdownExperience);

    const experience = this.experiences[this.countdownExperience];
    const interval =
      totalDuration / this.getDwellSteps(this.countdownExperience) + 0.01;
    experience.sendCountdownState(
      this.countdownStart + totalDuration,
      interval,
    );

    if (elapsed >= totalDuration) {
      this.completeCountdown();
    }
  };

  completeCountdown() {
    if (this.countdownExperience === undefined) {
      return null;
    }

    const experience = this.experiences[this.countdownExperience];

    if (experience.preview) {
      experience.handleEndPreview();
      this.appendPreview();
    } else {
      experience.requestExpand();
    }

    this.stopCountdown();
  }

  stopCountdown() {
    if (this.countdownInterval) {
      window.clearInterval(this.countdownInterval);
    }

    if (this.countdownExperience !== undefined) {
      const experience = this.experiences[this.countdownExperience];
      experience.sendCountdownState();
    }

    this.countdownInterval = undefined;
    this.countdownExperience = undefined;
    this.countdownStart = undefined;

    return null;
  }

  startCountdown(experience: number) {
    const interval =
      this.getDwellDuration(experience) / this.getDwellSteps(experience);

    this.countdownExperience = experience;
    this.countdownStart = Date.now();

    this.tickCountdown();

    this.countdownInterval = window.setInterval(this.tickCountdown, interval);

    return null;
  }

  getDwellDuration(experience: number): number {
    const customData = this.decisionPayload?.decisions.inline[experience]
      .contents[0].data.customData as KevelCustomData;
    if (customData.dwellTimerMs) {
      return asNumber(
        customData.dwellTimerMs || DEFAULT_DWELL_TIMER_MS,
        DEFAULT_DWELL_TIMER_MS,
      );
    }
    return DEFAULT_DWELL_TIMER_MS;
  }

  getDwellSteps(experience: number): number {
    const customData = this.decisionPayload?.decisions.inline[experience]
      .contents[0].data.customData as KevelCustomData;
    if (customData.dwellSteps) {
      return asNumber(customData.dwellSteps, COUNTDOWN_STEPS);
    }
    return COUNTDOWN_STEPS;
  }

  hoverCallback = (entries: readonly IntersectionObserverEntry[]) => {
    entries.forEach((entry, i) => {
      const target = entry.target as HTMLElement;
      if (entry.isIntersecting) {
        this.fullyVisibleExperiences.push(i);
        target.addEventListener('mouseover', this.startCountdownWithMap(i));
      } else {
        this.fullyVisibleExperiences = this.fullyVisibleExperiences.filter(
          exp => i !== exp,
        );
        if (!this.markers[i].active) {
          target.removeEventListener(
            'mouseover',
            this.startCountdownWithMap(i),
          );
        }
      }
    });
    return null;
  };

  observerCallback = (entries: readonly IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      const target = entry.target as HTMLElement;
      this.markers.forEach(marker => {
        if (marker.el === target) {
          marker.active = entry.isIntersecting;
        }
      });
    });

    // eslint-disable-next-line functional/no-let
    let setActive = false;
    this.markers.forEach((marker, i) => {
      const experience = this.experiences[i];
      const lastExperience = i > 0 ? this.experiences[i - 1] : null;
      const isFirstExperience = i === 0;
      const lastExperienceExpanded =
        lastExperience && lastExperience.expandCount >= AUTO_EXPAND_LIMIT;
      const canBeActive =
        (!experience.preview && experience.expandCount < AUTO_EXPAND_LIMIT) ||
        (experience.preview && (lastExperienceExpanded || isFirstExperience));

      if (canBeActive && !setActive && marker.active) {
        this.handleMarkerActivation(experience, i);
        setActive = true;
      } else if (!marker.active) {
        this.handleMarkerDeactivation(experience, i);
      }
    });
  };

  private handleMarkerActivation(
    experience: Readonly<Experience>,
    i: number,
  ): void {
    if (this.countdownExperience !== i) {
      this.stopCountdown();
      if (experience.expandCount < AUTO_EXPAND_LIMIT) {
        if (
          experience.hoverExpandable &&
          experience.contentElement &&
          this.experiences.length === 1
        ) {
          experience.contentElement.addEventListener(
            'mouseover',
            this.startCountdownWithMap(i),
          );
        } else {
          this.startCountdown(i);
        }
      }
    }
  }

  private handleMarkerDeactivation(
    experience: Readonly<Experience>,
    i: number,
  ): void {
    if (!this.fullyVisibleExperiences.includes(i)) {
      experience.contentElement?.removeEventListener(
        'mouseover',
        this.startCountdownWithMap(i),
      );
    }
    if (this.countdownExperience === i) {
      this.stopCountdown();
    }
  }

  private readonly scrollBeyondCallback: IntersectionObserverCallback =
    entries => {
      entries
        // Ignore non-intersecting elements
        .filter(entry => entry.isIntersecting)
        // Find the matching Experience for each entry
        .map(entry => this.markers.find(marker => marker.el === entry.target))
        // Ignore if we can't find the experience
        .filter(exp => exp && this.experiences[exp.index])
        // Trigger the scroll beyond handler on each matched experience
        .forEach(exp => this.experiences[exp?.index ?? 0].scrollBeyond());

      return null;
    };

  request() {
    if (this.requested) {
      return null;
    }

    this.targetEl = document.querySelector(this.targetSelector);
    if (!this.targetEl) {
      console.log('No target div found for', this.targetSelector);
      return null;
    }
    this.setupContainerViewableBeacon();
    if (this.options.isGoogleAmp || this.options.disableLazyLoading) {
      this.getExperiences();
    } else {
      fireOnFirstInView(
        this.targetEl,
        () => this.getExperiences(),
        this.options.rootElement,
      );
    }

    this.requested = true;
  }

  private readonly handleAdClick = () => {
    if (this.clickTrackerSent) {
      return null;
    }
    this.clickTrackerSent = true;
    fireClickTracker(this.options.clickTrackerUrl);
    return null;
  };

  private readonly resizeExperience = (
    experience: Readonly<Experience>,
    height: number,
  ) => {
    if (
      this.options.nestedIframe &&
      this.targetEl &&
      experience.contentElement
    ) {
      experience.contentElement.style.height = `${height}px`;
      resizeOwnIframe(
        this.targetEl.offsetHeight,
        !!this.options.nestedIframeShared,
        !!this.options.nestedIframeFullBleed,
        !!this.options.nestedIframeResizing,
      );
    }

    return null;
  };

  private insertPassback(passbackTag: string) {
    if (!this.targetEl) {
      return null;
    }

    const iframe = document.createElement('iframe');
    iframe.src = 'about:blank';

    iframe.style.width = this.options.passbackWidth || '100%';
    iframe.style.height = this.options.passbackHeight || '600px';
    iframe.setAttribute('scrolling', 'no');
    iframe.style.border = 'none';

    this.targetEl.appendChild(iframe);

    if (!iframe.contentWindow) {
      return;
    }
    iframe.contentWindow.document.open();
    iframe.contentWindow.document.write(passbackTag);
    iframe.contentWindow.document.close();
  }

  handlePreviewEnded = () => {
    this.appendPreview();
    return null;
  };

  private handleExperienceAddError(
    preview: boolean,
    experienceIdx: number,
  ): void {
    if (!preview && experienceIdx === 0) {
      this.callSlotFillResultCallback(false);
    }
  }

  private addExperience(preview = false, experienceIdx = 0) {
    const decisionsLength =
      this.decisionPayload?.decisions?.inline?.length ?? 0;
    if (
      !this.targetEl ||
      !this.decisionPayload ||
      experienceIdx >= decisionsLength
    ) {
      this.handleExperienceAddError(preview, experienceIdx);
      return null;
    }

    const optionsWithPreview = {
      ...this.options,
      preview,
    };
    const experience = new Experience({
      targetEl: this.targetEl,
      publisherNameIdentifier: this.publisherNameIdentifier,
      data: this.decisionPayload?.decisions.inline[experienceIdx],
      options: optionsWithPreview,
      resizeCallback: this.resizeExperience,
      siteId: this.siteId,
      zoneIds: this.zoneIds,
      handlePreviewEnded: this.handlePreviewEnded,
      onAdClick: this.handleAdClick,
      geoData: this.decisionPayload.location,
      keywords: [...getContextKeywords()],
      positionIndex:
        this.experienceLimit > 1 && decisionsLength > 1
          ? experienceIdx + 1
          : null,
    });
    this.experiences.push(experience);
    experience.render();
    if (experience.contentElement) {
      this.appendHolodeckLink();
    }
    if (experience.hoverExpandable && decisionsLength === 1) {
      experience.contentElement?.addEventListener('mouseleave', () => {
        this.stopCountdown();
      });
    }
    // Create marker that detects when bottom of experience is in view.
    const marker = document.createElement('div');
    marker.style.height = '0px';
    marker.classList.add('santinel');
    this.targetEl.appendChild(marker);
    this.markers.push({ el: marker, active: false, index: experienceIdx });
    if (this.scrollBeyondObserver && this.dwellObserver && this.hoverObserver) {
      this.scrollBeyondObserver.observe(marker);
      if (this.options.dwellExpandable) {
        if (
          experience.contentElement &&
          experience.hoverExpandable &&
          decisionsLength === 1
        ) {
          // Enable hover expandable experiences at bottom half of the viewport
          this.dwellObserver = new IntersectionObserver(this.observerCallback, {
            rootMargin: '-50% 0px 0px 0px',
          });
          this.hoverObserver.observe(experience.contentElement);
        }
        this.dwellObserver.observe(marker);
      }
    }
    if (!preview && experienceIdx === 0) {
      this.callSlotFillResultCallback(true);
    }
  }

  private buildHolodeckLink(): string {
    const sessionId = encodeURIComponent(getSessionId());
    return `https://holodeck.flippback.com/traffic?program=nativex&sessionId=${sessionId}`;
  }

  private appendHolodeckLink(): void {
    if (
      !this.targetEl ||
      environment === 'production' ||
      this.targetEl.querySelector('.flipp-holodeck-link')
    ) {
      return;
    }
    const link = document.createElement('a');
    link.classList.add('flipp-holodeck-link');
    link.href = this.buildHolodeckLink();
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = 'Open in Holodeck ↗';
    link.style.cssText =
      'display: block; font-size: 11px; color: #888; text-decoration: none; margin-top: 4px;';
    this.targetEl.appendChild(link);
  }

  private appendPreview() {
    const decisionsLength =
      this.decisionPayload?.decisions?.inline?.length ?? 0;
    if (!this.decisionPayload) {
      return null;
    }
    if (this.experienceLimit <= this.experiences.length) {
      return null;
    }

    if (decisionsLength <= this.experiences.length) {
      return null;
    }

    this.addExperience(true, this.experiences.length);
  }

  private setGAMStyle(decisionExist: boolean) {
    const minHeight = this.options.startCompact ? '610px' : 'initial';
    const style = `min-height: ${minHeight}; margin: 0 auto !important;
     height: auto !important; width: 100% !important;
            display: block !important; max-height: initial !important`;
    const parentElement = window.parent.document;
    setStyleForAdContainers(parentElement, style);
    const rootElement = this.options.rootElement;
    if (!rootElement) {
      const publisherBodyWidth = (() => {
        try {
          const topWidth = window.top?.document.body.clientWidth;
          return topWidth && topWidth > 0
            ? topWidth
            : parentElement.body.clientWidth;
        } catch {
          return parentElement.body.clientWidth;
        }
      })();
      const gamContainerWidth = Math.min(
        this.options.gamContainerWidth || -1,
        publisherBodyWidth,
      );
      setStyleForIframeContainers({
        style,
        decisionExist,
        callSlotFillResultCallback: args =>
          this.callSlotFillResultCallback(args),
        nestedIframeResizing: !!this.options.nestedIframeResizing,
        gamContainerWidth,
      });
    } else {
      setStyleForRootElements(parentElement, style, rootElement);
    }
  }

  private getGeolocatedExperience(body: Readonly<KevelRequest>) {
    this.queueRequestMadeBeacon();
    const campaignsResponse = this.options.campaigns
      ? Promise.resolve(JSON.parse(this.options.campaigns))
      : fetch('__campaignsURL', {
          method: 'POST',
          credentials: 'omit',
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'text/plain',
          },
        }).then(response => {
          if (!response.ok) {
            this.callSlotFillResultCallback(false);
            this.queueFailureBeacon(response);
          }
          return response.json();
        });

    campaignsResponse
      .then((data: Readonly<CampaignResponseBody>) => {
        this.applyKevelFeatureFlags(data);
        this.queueSuccessBeacon(data);
        executeBeaconCallback(
          this.options.beaconEventCallbacks,
          'servedImpression',
        );
        this.decisionPayload = data as CampaignResponseBody;

        if (this.options.mobileSwipe) {
          // V1 flyer decisions carry Mobile Swipe's V2 publication separately,
          // so Fixed Height keeps its V1 flyer; V2 decisions use publication
          const inlineDecision = data.decisions.inline?.[0];
          this.renderMobileSwipeExperience(
            inlineDecision?.mobileSwipePublication?.global_id ??
              inlineDecision?.publication?.global_id,
          );
          return;
        }

        const decisionExist =
          data.decisions &&
          typeof data.decisions === 'object' &&
          Object.keys(data.decisions).some(
            key =>
              key in data.decisions &&
              data.decisions[key as unknown as keyof typeof data.decisions], // The type is ugly, but it should be safe
          );

        if (isInGoogleAdsIframe()) {
          const isNestedIframe = !!this.options.nestedIframe;
          const crossOrigin = window.parent !== window;
          // eslint-disable-next-line functional/no-let
          let isGAMNestedIframe = false;
          try {
            isGAMNestedIframe =
              crossOrigin &&
              !!window.parent.document.querySelector(
                '.GoogleActiveViewElement, .GoogleActiveViewInnerContainer',
              );
          } catch (e) {
            isGAMNestedIframe = false;
            Logger.debug('Error checking for GAM nested iframe', e);
          }

          AnalyticsQueue.enqueue(
            createExperienceMeasuredTestBeacon({
              partnerNameIdentifier: this.publisherNameIdentifier,
              siteId: this.siteId,
              zoneId: this.zoneIds[0] ?? 0,
              postalCode: data.location?.postal_code,
              options: this.options,
              country: data.location?.country,
              testName: 'GAMContainerDetection',
              resultName: 'inGAMContainer',
              resultLabel: String(isGAMNestedIframe),
              resultPayload: {
                isNestedIframe,
                GAMNestedIframe: isGAMNestedIframe,
                crossOrigin,
              },
            }),
          );
          try {
            this.setGAMStyle(decisionExist);
          } catch (e) {
            if (e instanceof Error) {
              void logError(e.message, e.stack, window.location.href);
            } else {
              logUnknownError(e);
            }
          }
        }

        // Specifically for Postmedia we need to hide the header if no decision was returned from the API
        const header =
          this.siteId === 1179443 &&
          document.getElementById('flyerWidgetLabel');
        if (decisionExist && header) {
          header.style.display = 'block';
        }

        // If we have no decision and a passback, call the passback
        if (
          this.options.passbackTag &&
          (!data.decisions?.inline ||
            data.decisions.inline.length < 1 ||
            data.decisions.inline[0] === null)
        ) {
          this.callSlotFillResultCallback(false);
          const tag =
            typeof this.options.passbackTag === 'function'
              ? this.options.passbackTag()
              : this.options.passbackTag;

          if (typeof tag === 'object' && typeof tag.then === 'function') {
            // NOTE: We return here to ensure that exceptions bubble up
            return tag
              .then(t => {
                if (!t) {
                  throw new Error(JSON.stringify(t));
                }
                this.insertPassback(t);
                return null;
              })
              .catch(e => {
                const msg = e instanceof Error ? e.message : 'unknown';

                throw new Error(
                  'Passback promise fallthrough. Hiding experience: ' + msg,
                );
              });
          } else if (typeof tag === 'string' && tag) {
            this.insertPassback(tag);
          } else {
            throw new Error('Passback fallthrough. Hiding experience');
          }
          return;
        } else if (
          (this.options.nestedIframe && !data.decisions) ||
          !data.decisions.inline ||
          data.decisions.inline.length < 1 ||
          data.decisions.inline[0] === null
        ) {
          resizeOwnIframe(
            0,
            !!this.options.nestedIframeShared,
            !!this.options.nestedIframeFullBleed,
            !!this.options.nestedIframeResizing,
          );
        }
        this.addExperience();
        if (data.decisions.inline && data.decisions.inline.length > 1) {
          this.appendPreview();
        }
      })
      .catch((err: Readonly<Error>) => {
        // Passback fallthrough isn't truly an error, so don't send an upstream message about it
        if (err.message.startsWith('Passback')) {
          console.warn('Flipp Experience failed to load: ', err.message);
        } else {
          Logger.error('Slot error', err);
          this.queueErrorBeacon(err);
        }

        if (this.options.nestedIframe && this.experiences.length === 0) {
          resizeOwnIframe(
            0,
            !!this.options.nestedIframeShared,
            !!this.options.nestedIframeFullBleed,
            !!this.options.nestedIframeResizing,
          );
        }
        this.callSlotFillResultCallback(false);
        return null;
      });
  }

  private getMobileSwipeAutoEngageConfig():
    | { enabled?: boolean; intervalMs?: number }
    | undefined {
    const { mobileSwipeAutoEngageEnabled, mobileSwipeAutoEngageIntervalMs } =
      this.options;
    if (
      mobileSwipeAutoEngageEnabled === undefined &&
      mobileSwipeAutoEngageIntervalMs === undefined
    ) {
      return undefined;
    }
    return {
      enabled: mobileSwipeAutoEngageEnabled,
      intervalMs: mobileSwipeAutoEngageIntervalMs,
    };
  }

  private renderMobileSwipeExperience(publicationGlobalId?: string) {
    if (!this.targetEl) {
      this.callSlotFillResultCallback(false);
      return null;
    }
    const globalId = publicationGlobalId ?? this.options.publicationGlobalId;
    if (typeof globalId !== 'string') {
      Logger.error('MobileSwipe: publicationGlobalId is required');
      this.callSlotFillResultCallback(false);
      return null;
    }
    const decision = this.decisionPayload?.decisions?.inline?.[0];

    const ctaUrl = resolveMobileSwipeCtaUrl(
      (decision?.contents?.[0]?.data?.customData as KevelCustomData | undefined)
        ?.mobileSwipeCtaUrl,
    );

    const evTimer = resolveMobileSwipeEvTimerSeconds(
      (decision?.contents?.[0]?.data?.customData as KevelCustomData | undefined)
        ?.evTimerMs,
    );

    // customData is hand-entered in Kevel; pass only a non-empty string on.
    const traffickedIntlFmt = (
      decision?.contents?.[0]?.data?.customData as KevelCustomData | undefined
    )?.campaignIntlFmt;
    const campaignIntlFmt =
      typeof traffickedIntlFmt === 'string' && traffickedIntlFmt
        ? traffickedIntlFmt
        : undefined;

    const dynamicModule = resolveMobileSwipeDynamicModule({
      customData: decision?.contents?.[0]?.data?.customData as
        | KevelCustomData
        | undefined,
      nearestStores: decision?.nearestStores,
    });

    const autoEngage = this.getMobileSwipeAutoEngageConfig();

    const mobileSwipe = new MobileSwipeExperience({
      targetEl: this.targetEl,
      publicationGlobalId: globalId,
      siteId: this.siteId,
      zoneId: this.zoneIds[0],
      sessionId: getSessionId(),
      geoData: this.decisionPayload?.location,
      storefront: decision?.storefront
        ? {
            flyer_id: decision.storefront.flyer_id,
            flyer_run_id: decision.storefront.flyer_run_id,
            flyer_type_id: decision.storefront.flyer_type_id,
            merchant_id: decision.storefront.merchant_id,
            sfml_uuid: decision.storefront.sfml_uuid,
          }
        : undefined,
      decision: decision
        ? {
            campaignId: decision.campaignId,
            advertiserId: decision.advertiserId,
            creativeId: decision.creativeId,
            flightId: decision.flightId,
            adId: decision.adId,
            siteId: asNumber(this.siteId),
            zoneId: asNumber(this.zoneIds[0]),
          }
        : undefined,
      inlineDecision: decision,
      site: getSiteDomain(this.options.overrideHostname),
      integrationType: getIntegrationType(this.options),
      autoEngage,
      ctaUrl,
      evTimer,
      campaignIntlFmt,
      dynamicModule,
      onAdClick: this.handleAdClick,
      frameHeight: this.options.mobileSwipeFrameHeight,
    });
    const filled = mobileSwipe.render();
    if (filled) {
      this.sendMobileSwipeAutoEngageTestBeacon(autoEngage);
    }
    this.callSlotFillResultCallback(filled);
    return null;
  }

  /**
   * CF-758 auto-swipe A/B test: tags each served Mobile Swipe with its
   * auto-swipe group. V2 events have no field for the auto-swipe state, so
   * analytics joins this beacon to the unit's V2 events on session id.
   * Sent only while the test's flag or URL param sets auto-swipe explicitly.
   */
  private sendMobileSwipeAutoEngageTestBeacon(
    autoEngage?: Readonly<{ enabled?: boolean; intervalMs?: number }>,
  ) {
    if (autoEngage?.enabled === undefined) {
      return;
    }

    AnalyticsQueue.enqueue(
      createExperienceMeasuredTestBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        siteId: this.siteId,
        zoneId: this.zoneIds[0] ?? 0,
        options: this.options,
        postalCode: getPostalCode(),
        country: getCountry(),
        testName: 'MobileSwipeAutoEngage',
        resultName: 'Served',
        resultLabel: autoEngage.enabled ? 'on' : 'off',
        resultPayload: {
          intervalMs:
            autoEngage.intervalMs ??
            MOBILE_SWIPE_AUTO_ENGAGE_DEFAULT_INTERVAL_MS,
        },
      }),
    );
  }

  private getExperiences() {
    if (
      this.options.mobileSwipe &&
      typeof this.options.publicationGlobalId === 'string'
    ) {
      this.renderMobileSwipeExperience(this.options.publicationGlobalId);
      return null;
    }

    const placement: KevelPlacement = {
      divName: TARGET_NAME,
      networkId: NETWORK_ID,
      siteId: this.siteId,
      adTypes: AD_TYPES,
      count: this.experienceLimit,
    };

    const url = new URL(window.location.toString());

    // If adId is provided, use it (URL param takes priority over options)
    const adId =
      asNumber(url.searchParams.get('flipp-test-ad-id')) ||
      this.options.adId ||
      undefined;
    if (adId && !isNaN(adId)) {
      placement['adId'] = adId;
      // When requesting a specific ad by ID, skip targeting filters
      // since we want to display exactly this ad
      placement['skipFilters'] = true;
    }

    if (
      this.zoneIds !== undefined &&
      this.zoneIds != null &&
      this.zoneIds.length > 0
    ) {
      placement['zoneIds'] = this.zoneIds;
    }

    const properties: {
      contentCode?: string | null;
      [key: string]: unknown;
    } = {};

    // Forward a preview code for targeting
    const contentCode = this.fallbackRequested
      ? FIXED_HEIGHT_UNIT_FALLBACK_CONTENT_CODE
      : url.searchParams.get('flipp-content-code') ??
        this.options.flippContentCode;
    if (contentCode && contentCode.length > 0) {
      properties['contentCode'] = contentCode.slice(0, 32);
    }

    if (this.options.mediaPartnerDescriptiveValueTag) {
      properties['mediaPartnerDescriptiveValueTag'] =
        this.options.mediaPartnerDescriptiveValueTag.slice(0, 32);
    }

    placement['properties'] = properties;

    if (this.options.mobileSwipe === true) {
      placement['options'] = { mobileSwipe: true };
    }

    const keywords = [
      ...getContextKeywords(),
      ...checkPageForKeywords(badKeywords, true),
    ].filter(e => typeof e === 'string');

    const body: KevelRequest = {
      placements: [placement],
      url: this.getPageUrl(),
      keywords,
    };

    const userKey = getUserKey(this.options);
    if (userKey) {
      body.user = { key: userKey };
    }
    body.sessionId = getSessionId();
    if (this.options.ip) {
      body.ip = this.options.ip;
    }
    this.getGeolocatedExperience(body);

    this.queueRequestContextBeacon(keywords);

    return null;
  }

  private getPageUrl() {
    if (this.options.overrideHostname) {
      return this.options.overrideHostname;
    }
    if (isInIframe()) {
      const url = new URL(document.referrer);
      return url.origin + url.pathname;
    }
    return window.location.origin + window.location.pathname;
  }

  private queueRequestContextBeacon(keywords: readonly string[]) {
    const url = this.getPageUrl();
    const urlParams = new URLSearchParams(window.location.search);
    const queryParams =
      urlParams.size === 0 ? [] : Array.from(urlParams.entries());

    AnalyticsQueue.enqueue(
      createDecisionRequestContextBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        keywords: [...keywords],
        category: getContextCategory(),
        pageUrl: url,
        queryParams,
        options: this.options,
        postalCode: getPostalCode(),
        country: getCountry(),
      }),
    );
    Logger.debug('Analytics sendDecisionRequestContext');
    return null;
  }

  private queueRequestMadeBeacon() {
    AnalyticsQueue.enqueue(
      createDecisionRequestMadeBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        options: this.options,
        postalCode: getPostalCode(),
        country: getCountry(),
      }),
    );
    Logger.debug('Analytics sendDecisionRequestMade');
    return null;
  }

  private queueFailureBeacon(response: Response) {
    AnalyticsQueue.enqueue(
      createDecisionResponseFailureBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        errorMessage: 'Campaigns request failed',
        responseCode: response.status,
        options: this.options,
        postalCode: getPostalCode(),
        country: getCountry(),
      }),
    );
    Logger.debug('Analytics sendDecisionRequestFailure');
    return null;
  }

  // Get reliable viewport height using visualViewport API when available
  // This avoids issues with dynamic URL bars on mobile browsers
  private getViewportHeight(win: Window): number {
    if (win.visualViewport) {
      return win.visualViewport.height;
    }
    return win.innerHeight;
  }

  private setupContainerViewableBeacon() {
    if (!this.targetEl || this.hasFiredContainerBeacon) return;
    const iframeInParent = this.options.nestedIframe
      ? thisIframeInParent()
      : null;
    const observerRoot = iframeInParent ? window.parent.document : null;
    const elementToObserve = iframeInParent || this.targetEl;
    const viewportWindow = iframeInParent ? window.parent : window;
    const thresholds = Array.from({ length: 21 }, (_, i) => i * 0.05);

    this.containerViewableObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const elementHeight = entry.boundingClientRect.height;
          const visibleHeight = entry.intersectionRect.height;
          const viewportHeight = this.getViewportHeight(viewportWindow);

          const containerVisibleRatio =
            elementHeight > 0 ? visibleHeight / elementHeight : 0;

          const viewportOccupiedRatio =
            viewportHeight > 0 ? visibleHeight / viewportHeight : 0;

          const isViewable =
            (containerVisibleRatio >= 0.5 || viewportOccupiedRatio >= 0.5) &&
            elementHeight >= 100 &&
            visibleHeight >= 100;

          if (
            !this.hasFiredContainerBeacon &&
            isViewable &&
            !this.containerViewableTimer
          ) {
            this.containerViewableTimer = window.setTimeout(() => {
              if (!this.hasFiredContainerBeacon) {
                this.hasFiredContainerBeacon = true;

                const isSendingV2 = !!this.latestDecisionData;
                const skipV1Beacon = this.shouldSkipV1Beacon(isSendingV2);
                const v1Beacon = skipV1Beacon
                  ? undefined
                  : createContainerViewableImpressionBeacon({
                      partnerNameIdentifier: this.publisherNameIdentifier,
                      siteId: this.siteId,
                      zoneId: this.zoneIds[0] || 0,
                      options: this.options,
                      postalCode: getPostalCode(),
                      country: getCountry(),
                    });
                if (!skipV1Beacon) {
                  AnalyticsQueue.enqueue(v1Beacon);
                }
                if (isSendingV2) {
                  this.v2BeaconGenerator.sendContainerViewableImpression(
                    v1Beacon?.shopperPlatformBase.uuid,
                  );
                }
                Logger.debug('Fired container vimp');
                this.containerViewableObserver?.disconnect();
              }
              this.containerViewableTimer = undefined;
            }, 1000);
          } else if (!isViewable && this.containerViewableTimer) {
            window.clearTimeout(this.containerViewableTimer);
            this.containerViewableTimer = undefined;
          }
        });
      },
      { threshold: thresholds, root: observerRoot },
    );

    this.containerViewableObserver.observe(elementToObserve);
  }

  private queueSuccessBeacon(data: Readonly<CampaignResponseBody>) {
    const getCustomData = (
      data: Readonly<CampaignResponseBody>,
    ): { url: string; campaignNameIdentifier: string } => {
      // eslint-disable-next-line functional/no-let
      let newCustomData = {
        url: 'no-campaign-url-nor-ulp',
        campaignNameIdentifier: 'no-campaign-name-identifier',
      };
      if (data.decisions?.inline && data.decisions.inline.length > 0) {
        const customData = data.decisions.inline[0].contents[0].data
          .customData as KevelCustomData;
        const customUrl = customData?.campaignConfigUrl ?? customData?.ulpUrl;
        newCustomData = {
          url: customUrl ?? 'no-campaign-url-nor-ulp',
          campaignNameIdentifier:
            customData?.campaignNameIdentifier ?? 'no-campaign-name-identifier',
        };
      }
      return newCustomData;
    };

    const hasContent =
      data.decisions.inline && data.decisions.inline[0].contents.length > 0;
    const { url, campaignNameIdentifier } = getCustomData(data);
    if (hasContent) {
      const kevelMetadata = data.decisions.inline[0].contents[0].data
        .customData as KevelCustomData;
      const pageUrl = new URL(window.location.toString());
      this.experienceLimit =
        Number(pageUrl.searchParams.get('flipp-experience-limit')) ||
        Number(kevelMetadata.experienceLimit) ||
        this.options.experienceLimit ||
        DEFAULT_EXPERIENCE_COUNT;
    }
    const hasMultipleExperiences =
      this.experienceLimit > 1 && data.decisions.inline?.length > 1;

    const decisions = data.decisions.inline
      ? [
          {
            campaignId: data.decisions.inline[0].campaignId,
            siteId: asNumber(this.siteId),
            zoneId: asNumber(this.zoneIds[0]) || 0,
            adId: data.decisions.inline[0].adId,
            creativeId: data.decisions.inline[0].creativeId,
            flightId: data.decisions.inline[0].flightId,
            advertiserId: data.decisions.inline[0].advertiserId,
            customUrl: hasContent ? { string: url } : null,
            positionIndex: hasMultipleExperiences ? { int: 1 } : null,
            flyerId: asNumber(data.decisions.inline[0].storefront?.flyer_id),
            merchantId: asNumber(
              data.decisions.inline[0].storefront?.merchant_id,
            ),
          },
        ]
      : [];

    const loc = data?.location;
    const enableV2Schema =
      !!hasContent &&
      this.shouldSendV2Beacons(
        data.decisions.inline[0].contents[0].data.customData as KevelCustomData,
      );
    const skipV1Beacon = this.shouldSkipV1Beacon(enableV2Schema);
    const v1Beacon = skipV1Beacon
      ? undefined
      : createDecisionResponseSuccessBeacon({
          campaignNameIdentifier,
          partnerNameIdentifier: this.publisherNameIdentifier,
          siteId: this.siteId,
          zoneId: this.zoneIds[0] || 0,
          country: loc?.country,
          postalCode: loc?.postal_code,
          decisions,
          options: this.options,
        });
    if (!skipV1Beacon) {
      AnalyticsQueue.enqueue(v1Beacon);
    }
    if (enableV2Schema) {
      this.latestDecisionData = data;
      this.v2BeaconGenerator.sendDecisionResponseSuccess(
        v1Beacon?.shopperPlatformBase.uuid,
      );
    }
    Logger.debug('Analytics sendDecisionRequestSuccess');
    return null;
  }

  private queueErrorBeacon(err: Readonly<Error>) {
    AnalyticsQueue.enqueue(
      createDecisionResponseFailureBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        errorMessage: err.message + ' - ' + (err.stack ?? ''),
        responseCode: 0,
        options: this.options,
        postalCode: getPostalCode(),
        country: getCountry(),
      }),
    );
    Logger.debug('Analytics sendDecisionRequestFailure');
    return null;
  }

  sendImpression = ({
    cpm,
    postalCode,
    campaignId,
  }: Readonly<{ cpm: number; postalCode: string; campaignId: number }>) => {
    Logger.debug('Analytics sendSlotimpressionExperience');
    if (this.options.campaigns) {
      const data = JSON.parse(
        this.options.campaigns,
      ) as Readonly<CampaignResponseBody>;
      this.applyKevelFeatureFlags(data);
      const inlineDecision = data.decisions.inline?.[0];

      const decisions = inlineDecision
        ? [
            {
              campaignId: inlineDecision.campaignId,
              siteId: asNumber(this.siteId),
              zoneId: asNumber(this.zoneIds[0]) || 0,
              adId: inlineDecision.adId,
              creativeId: inlineDecision.creativeId,
              flightId: inlineDecision.flightId,
              advertiserId: inlineDecision.advertiserId,
              customUrl: null,
              positionIndex: null,
              flyerId: inlineDecision.storefront?.flyer_id ?? 0,
              merchantId: inlineDecision.storefront?.merchant_id ?? 0,
            },
          ]
        : [];
      executeBeaconCallback(
        this.options.beaconEventCallbacks,
        'servedImpression',
      );
      const hasContent = !!inlineDecision?.contents?.[0];
      const enableV2Schema =
        hasContent &&
        this.shouldSendV2Beacons(
          inlineDecision?.contents?.[0]?.data?.customData as
            | KevelCustomData
            | undefined,
        );
      const skipV1Beacon = this.shouldSkipV1Beacon(enableV2Schema);
      const v1Beacon = skipV1Beacon
        ? undefined
        : createSlotImpressionExperienceBeacon({
            partnerNameIdentifier: this.publisherNameIdentifier,
            siteId: this.siteId,
            zoneId: this.zoneIds[0] || 0,
            options: this.options,
            cpm,
            postalCode,
            campaignId,
            decisions,
            country: data.location?.country,
          });
      if (!skipV1Beacon) {
        AnalyticsQueue.enqueue(v1Beacon);
      }
      if (enableV2Schema) {
        this.latestDecisionData = data;
        this.v2BeaconGenerator.sendSlotImpressionExperience(
          v1Beacon?.shopperPlatformBase.uuid,
        );
      }
      if (inlineDecision) {
        Tags.addTagsFromCampaignResponse(
          inlineDecision,
          {
            startCompact: !!this.options.startCompact,
          },
          data.location,
        );
        Tags.sendServedImpression();
      }
    }
    return null;
  };
}

// for the case if bot is detected on the frontend for openrtb and prebid integrations
export class MockedSlot {
  sendImpression() {
    return null;
  }
}

export default Slot;
